import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { checkReadyToBid } from '@/components/GridItemCard/Card/helpers/checkReadyToBid';
import { ItemCardComponent, ItemCardInfo } from '@/components/GridItemCard/types/types';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useAppSelector } from '@/redux/hooks';
import { getUserIsInGoodStanding } from '@/redux/modules/account/user/user.selectors';
import { getBidLimitBalance } from '@/redux/modules/bidLimit/bidLimit.selectors';
import {
    getRegistrationApproved,
    getRegistrationApprovedWithLimit,
    getRegistrationBidLimit,
    getRegistrationBlocked,
    getRegistrationDeclined,
    getRegistrationSuspended,
} from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import {
    getCatalogNetworkStatus,
    getLatestMessageTimestamp,
    getLiveCatalogClosed,
    getLiveCatalogCurrentItemId,
    getLiveCatalogCurrentItemIndex,
    getLiveCatalogStatus,
    getLiveCatalogWon,
    getLiveItemDataCurrentAskPrice,
} from '@/redux/modules/console';
import {
    getItemSummaryCatalogId,
    getItemSummaryCatalogStatus,
    getItemSummaryIndex,
    getItemSummaryLeadingBid,
    getItemSummaryPromoted,
    getItemSummarySalePrice,
    getItemSummaryStartPrice,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { ItemCardSource, useTrackItemCardClicked } from '@/utils/amplitude/analytics';
import { isMyBidsPage, isSavedItemsPage } from '@/utils/urls';

import { CardGridBody } from './components/CardGridBody/CardGridBody';
import { CardImage } from './components/CardImage/CardImage';

const MyItemsUpcomingItemCard: ItemCardComponent = ({ className, itemId }) => {
    const trackItemCardClicked = useTrackItemCardClicked();

    const { pathname } = useLocation();

    const isMobile = useIsMobileLayout();

    const [cardHovered, setCardHovered] = useState(false);
    const [linkHovered, setLinkHovered] = useState(false);

    const accountInGoodStanding = useAppSelector(getUserIsInGoodStanding);
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const catalogStatus = useAppSelector((state) => getItemSummaryCatalogStatus(state, itemId));
    const index = useAppSelector((state) => getItemSummaryIndex(state, itemId));
    const promoted = useAppSelector((state) => getItemSummaryPromoted(state, itemId));
    const leadingBid = useAppSelector((state) => getItemSummaryLeadingBid(state, itemId));
    const salePrice = useAppSelector((state) => getItemSummarySalePrice(state, itemId));
    const startPrice = useAppSelector((state) => getItemSummaryStartPrice(state, itemId));

    const approved = useAppSelector((state) => getRegistrationApproved(state, catalogId));
    const bidLimit = useAppSelector((state) => getRegistrationBidLimit(state, catalogId));
    const blockedByAuctioneer = useAppSelector((state) => getRegistrationBlocked(state, catalogId));
    const declined = useAppSelector((state) => getRegistrationDeclined(state, catalogId));
    const limited = useAppSelector((state) => getRegistrationApprovedWithLimit(state, catalogId));
    const suspended = useAppSelector((state) => getRegistrationSuspended(state, catalogId));

    const currentItemIndex = useAppSelector((state) => getLiveCatalogCurrentItemIndex(state, catalogId));
    const closed = useAppSelector((state) => getLiveCatalogClosed(state, catalogId));
    const won = useAppSelector((state) => getLiveCatalogWon(state, catalogId));
    const status = useAppSelector((state) => getLiveCatalogStatus(state, catalogId));
    const currentItem = useAppSelector((state) => getLiveCatalogCurrentItemId(state, catalogId));
    const currentAskPrice = useAppSelector((state) => getLiveItemDataCurrentAskPrice(state, itemId));
    const liveUpdateTimestamp = useAppSelector((state) => getLatestMessageTimestamp(state, catalogId));
    const bidLimitBalance = useAppSelector((state) => getBidLimitBalance(state, catalogId));
    const networkStatus = useAppSelector((state) => getCatalogNetworkStatus(state, catalogId));

    const currentPrice = leadingBid || salePrice || startPrice || 0;
    const overBidLimit = limited ? bidLimitBalance + currentPrice > bidLimit : false;

    const comingUp = catalogStatus === 'live' && currentItemIndex <= index;
    const currentItemLive = comingUp && currentItemIndex === index;
    const isDone = catalogStatus === 'done';
    const showBidStatus = !isDone;

    const isStoppedFromBidding =
        !accountInGoodStanding ||
        (!approved && !limited) ||
        suspended ||
        declined ||
        blockedByAuctioneer ||
        overBidLimit ||
        !checkReadyToBid({
            catalogId,
            closed,
            currentAskPrice,
            currentItem,
            itemId,
            liveUpdateTimestamp,
            networkStatus,
            status,
            won,
        });

    const onCardClick = useCallback(() => {
        if (isSavedItemsPage(pathname)) {
            trackItemCardClicked({
                catalogId,
                itemId,
                promoted,
                source: ItemCardSource.MyItemsPageFavorites,
            });
        } else if (isMyBidsPage(pathname)) {
            trackItemCardClicked({
                catalogId,
                itemId,
                promoted,
                source: ItemCardSource.MyItemsPageBids,
            });
        }
    }, [catalogId, itemId, pathname, promoted, trackItemCardClicked]);

    const enterHoverState = useCallback(() => {
        setCardHovered(true);
    }, []);

    const leaveHoverState = useCallback(() => {
        setCardHovered(false);
    }, []);

    const enterLinkHoverState = useCallback(() => {
        setLinkHovered(true);
    }, []);

    const leaveLinkHoverState = useCallback(() => {
        setLinkHovered(false);
    }, []);

    return (
        <article
            className="relative"
            data-testid={`saved-item-card-${itemId}`}
            onClick={onCardClick}
            onMouseEnter={enterHoverState}
            onMouseLeave={leaveHoverState}
        >
            <section
                className={cn(
                    "grid [grid-template-columns:1fr] [grid-template-rows:min-content_min-content_min-content] justify-center [grid-template-areas:'itemimage'_'.'_'alldetailsformobilelayout'] xsMax:[grid-template-columns:1fr_4fr] xsMax:gap-x-sm xsMax:[grid-template-areas:'itemimage_alldetailsformobilelayout']",
                    isDone
                        ? 'xsMax:[grid-template-rows:min-content_11px_min-content_4px_min-content_2px_min-content_0]'
                        : 'xsMax:[grid-template-rows:min-content_min-content]',
                    currentItemLive && 'bg-surface-100 xsMax:m-0 xsMax:bg-transparent xsMax:p-0',
                    className
                )}
                data-testid="saved-items-card-grid"
            >
                <CardImage
                    enterLinkHoverState={enterLinkHoverState}
                    isMobile={isMobile}
                    itemId={itemId}
                    leaveLinkHoverState={leaveLinkHoverState}
                    showBidStatus={showBidStatus}
                />
                <CardGridBody
                    cardHovered={cardHovered}
                    enterLinkHoverState={enterLinkHoverState}
                    isCurrentLiveitem={currentItemLive}
                    isMobile={isMobile}
                    isStoppedFromBidding={isStoppedFromBidding}
                    itemId={itemId}
                    leaveLinkHoverState={leaveLinkHoverState}
                    linkHovered={linkHovered}
                />
            </section>
        </article>
    );
};

const info: ItemCardInfo = {
    ItemCardComponent: MyItemsUpcomingItemCard,
};

export default info;
